import React from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import EngineerAdmin from "@/pages/EngineerAdmin";

/**
 * MVP-ENG-001 · Legacy /settings/engineers route guard.
 *
 * The legacy route stays functional for the `installer` role (they see
 * the historic `<InstallerShell>` shell). Operations users are
 * redirected to the canonical `/operations/engineers` workspace so we
 * never bleed the installer chrome into Ops navigation. The current
 * query string is preserved so deep-links like `?highlight=<id>` are
 * carried across the redirect.
 */
export default function EngineersRouteGuard() {
    const { user } = useAuth();
    const location = useLocation();
    const [params] = useSearchParams();

    // FIELD-001 · Ordinary Engineers must not reach the engineer-admin
    // shell. Backend endpoints already refuse them; this closes the
    // frontend door so the URL is unreachable via direct navigation.
    // Engineer administration is exclusively an Operations concern with
    // the `manage_engineers` permission.
    const perms = user?.permissions || [];
    const isEngineerRole = (user?.role || "").toLowerCase() === "installer";
    const hasAdminPerm = perms.includes("manage_engineers");
    if (isEngineerRole && !hasAdminPerm) {
        return (
            <div className="mx-auto max-w-lg mt-20 p-6 rounded-xl border border-[#E5E7EB] bg-white text-center" data-testid="engineers-admin-refused">
                <div className="text-lg font-medium text-[#131B2F]">Engineer administration is not available for your account.</div>
                <p className="mt-2 text-sm text-[#4B5565]">Please ask an Operations team member if you need to update the roster.</p>
            </div>
        );
    }

    if (user?.role === "operations") {
        const forwarded = new URLSearchParams(params);
        // FIELD-001C1A · Legacy Ops entry-points that carried
        // `?admin=1` are funnelled directly to the canonical
        // Administration surface (`/operations/administration/field-team`)
        // rather than the read-only Resource Management workspace.
        // Any other query params ride along verbatim.
        if (forwarded.get("admin") === "1") {
            forwarded.delete("admin");
            forwarded.delete("tab");
            const qs = forwarded.toString();
            return (
                <Navigate
                    to={`/operations/administration/field-team${qs ? `?${qs}` : ""}`}
                    replace
                    state={{ from: location }}
                />
            );
        }
        const qs = forwarded.toString();
        return (
            <Navigate
                to={`/operations/resources${qs ? `?${qs}` : ""}`}
                replace
                state={{ from: location }}
            />
        );
    }
    // Installer / other authorised roles keep the historic workspace.
    return <EngineerAdmin />;
}
